import { useCallback, useEffect, useReducer, useState } from "react";
import { cloneDeep } from "lodash";

// MUI
import { Link } from "@mui/material";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// Third Party
import clsx from "clsx";

// Components
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import EligibilityRequirementsDialog from "../EligibilityRequirementsDialog";

// Helpers
import { isRequirementsInError } from "../../helpers/spiffDefinition";

// Types
import { ESpiffTypes, IEligibilityRequirement } from "../../types/spiffSetup";
import {
	EActionType,
	eligibilityRequirementsReducer,
	INITIAL_RULE,
	INITIAL_STATE,
	INITIAL_TIER,
	INITIAL_STATE_DEP,
	INITIAL_STATE_OP_CODES,
} from "../EligibilityRequirementsDialog/reducer";
import {
	eligibilityRequirementsSalesSchema,
	eligibilityRequirementsTradesSchema,
	eligibilityRequirementsOpCodesSchema,
	eligibilityRequirementsDepartmentSchema,
	TErrors,
} from "../EligibilityRequirementsDialog/validation";
import { BuildVariableOperationsGpsBonusSetup } from "@/PayPlans/helpers/DefaultSections";
import { IVariableOperationsGpsBonusSetup } from "@/PayPlans/types/sections/variableOperationsGpsBonus";
import { useErrors } from "@/Common/hooks/Errors";

const getInitialState = (
	requirements: IEligibilityRequirement | IVariableOperationsGpsBonusSetup,
	type: string,
	franchises: string[] = []
): IEligibilityRequirement | IVariableOperationsGpsBonusSetup => {
	if (!requirements) {
		switch (type) {
			case ESpiffTypes.GPS_COMPLIANCE:
				return BuildVariableOperationsGpsBonusSetup({ franchises });
			case ESpiffTypes.DEPARTMENT:
				return cloneDeep(INITIAL_STATE_DEP);
			case ESpiffTypes.OP_CODES:
				return cloneDeep(INITIAL_STATE_OP_CODES);
			default:
				return INITIAL_STATE;
		}
	}

	return {
		...requirements,
		...((requirements as IEligibilityRequirement)?.rules?.length === 0 && type === "Sales" ? { rules: [INITIAL_RULE] } : {}),
		...((requirements as IEligibilityRequirement)?.tiers?.length === 0 ? { tiers: [INITIAL_TIER] } : {}),
		...(!(requirements as IEligibilityRequirement)?.pvrType ? { pvrType: (requirements as IEligibilityRequirement).pvrType } : {}),
		...(type === "Trades" ? { franchises: (requirements as IEligibilityRequirement).franchises } : { franchises: [] }),
	};
};

interface DialogCellProps {
	title: string;
	requirements: IEligibilityRequirement | IVariableOperationsGpsBonusSetup;
	spiffType: string;
	editModeActive: boolean;
	editComplete?: (req: IEligibilityRequirement | IVariableOperationsGpsBonusSetup) => void;
}

const DialogCell = (props: DialogCellProps) => {
	const location = useSelector((stat: RootState) => stat.location.location);
	const { title, requirements, spiffType, editModeActive, editComplete } = props;
	const [eligibilityDialogOpen, setEligibilityDialogOpen] = useState(false);
	const [clickCount, setClickCount] = useState(1);
	const [state, dispatch] = useReducer(eligibilityRequirementsReducer, undefined, () =>
		getInitialState(requirements, spiffType, location?.franchises)
	);

	const {
		errors: hookErrors,
		getErrorKey,
		hasError,
		hasSomeError,
		addError,
		addErrors,
		clearError,
		clearErrors,
		removeError,
		removeErrors,
	} = useErrors();

	const useSectionsErrors = useCallback(
		() => ({
			errors: hookErrors,
			getErrorKey,
			hasError,
			hasSomeError,
			clearErrors,
			addSectionError: addError,
			addSectionErrors: addErrors,
			removeSectionError: removeError,
			removeSectionErrors: removeErrors,
			clearSectionErrors: clearError,
		}),
		[hookErrors, getErrorKey, hasError, hasSomeError, addError, addErrors, clearError, clearErrors, removeError, removeErrors]
	);
	const [errors, setErrors] = useState<TErrors>(null);
	const [extraErrors, setExtraErrors] = useState<Map<any, any> | null>(null);

	const TIME_BETWEEN_CLICKS = 500;

	const handleRulesAndDefinitionsDialogOpen = () => {
		setTimeout(() => {
			if (clickCount !== 1) {
				return;
			}
			setEligibilityDialogOpen((prev) => !prev);
			setClickCount(1);
		}, TIME_BETWEEN_CLICKS);
		setClickCount((prev) => prev + 1);
	};

	const handleRulesAndDefinitionsDialogClose = () => {
		setEligibilityDialogOpen(false);
		setClickCount(1);
	};

	useEffect(() => {
		if (eligibilityDialogOpen) {
			setExtraErrors(hookErrors);
		}
		return () => {};
	}, [hookErrors]);

	useEffect(() => {
		let validation;
		if (spiffType === ESpiffTypes.SALES) {
			validation = eligibilityRequirementsSalesSchema.validate(
				{ rules: (state as IEligibilityRequirement).rules },
				{
					allowUnknown: true,
					presence: "optional",
					abortEarly: false,
				}
			);
			setErrors(validation?.error ?? null);
		}
		if (spiffType === ESpiffTypes.TRADES) {
			validation = eligibilityRequirementsTradesSchema.validate(state, {
				allowUnknown: true,
				presence: "optional",
				abortEarly: false,
			});
			setErrors(validation?.error ?? null);
		}
		if (spiffType === "Op Codes") {
			validation = eligibilityRequirementsOpCodesSchema.validate(state, {
				allowUnknown: true,
				presence: "optional",
				abortEarly: false,
			});
			setErrors(validation?.error ?? null);
		}
		if (spiffType === "Department") {
			validation = eligibilityRequirementsDepartmentSchema.validate(state, {
				allowUnknown: true,
				presence: "optional",
				abortEarly: false,
			});
			setErrors(validation?.error ?? null);
		}

		if (editComplete) {
			editComplete(state);
		}

		return () => {
			setErrors(null);
		};
	}, [state]);

	useEffect(() => {
		if (!eligibilityDialogOpen) {
			dispatch({ type: EActionType.RESET, payload: { franchises: location?.franchises ?? [], spiffType } });
		}
		return undefined;
	}, [eligibilityDialogOpen, spiffType]);

	useEffect(() => {
		if (editModeActive) {
			return;
		}
		dispatch({ type: EActionType.UPDATE_ALL, payload: requirements });
		setExtraErrors(null);
	}, [editModeActive, requirements]);

	return (
		<>
			<TypographyCell>
				<Link
					href="#"
					underline="always"
					className={clsx("!text-[12px] !font-sans !underline", {
						"!text-primary": !isRequirementsInError(state, spiffType, extraErrors),
						"!decoration-primary": !isRequirementsInError(state, spiffType, extraErrors),
						"!text-error": isRequirementsInError(state, spiffType, extraErrors),
						"!decoration-error": isRequirementsInError(state, spiffType, extraErrors),
					})}
					onClick={() => {
						handleRulesAndDefinitionsDialogOpen();
					}}
				>
					Rules and Tier Definitions
				</Link>
			</TypographyCell>
			<EligibilityRequirementsDialog
				close={() => handleRulesAndDefinitionsDialogClose()}
				title={title}
				errorTitle={"New Spiff Title"}
				spiffType={spiffType}
				isEditMode={editModeActive}
				dispatch={dispatch}
				state={state}
				errors={errors}
				useSectionsErrors={useSectionsErrors}
				isOpen={eligibilityDialogOpen}
				oems={location?.oems ?? []}
			/>
		</>
	);
};

export default DialogCell;
